import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/app/Dashboard";
import Tasks from "@/pages/app/Tasks";
import TaskDetail from "@/pages/app/TaskDetail";
import TaskWorkspace from "@/pages/app/TaskWorkspace";
import Applications from "@/pages/app/Applications";
import Wallet from "@/pages/app/Wallet";
import Earnings from "@/pages/app/Earnings";
import Leaderboard from "@/pages/app/Leaderboard";
import Referrals from "@/pages/app/Referrals";
import Profile from "@/pages/app/Profile";
import Notifications from "@/pages/app/Notifications";
import MessageCentre from "@/pages/app/MessageCentre";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminAfriSelection from "@/pages/admin/AdminAfriSelection";
import AdminTaskSubmissions from "@/pages/admin/AdminTaskSubmissions";
import AdminTasks from "@/pages/admin/AdminTasks";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminWithdrawals from "@/pages/admin/AdminWithdrawals";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminSelectionLogs from "@/pages/admin/AdminSelectionLogs";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import Waitlist from "@/pages/Waitlist";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Disclaimer from "@/pages/legal/Disclaimer";
import POC from "@/pages/POC";
import ProfileSetup from "@/pages/app/ProfileSetup";
import { EnterPhone, EnterPin, VerifyCode, CreateAccount, SetPin, ResetPin } from "@/pages/auth/AuthPages";
import AppToaster from "@/components/AppToaster";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import { getSessionToken, getWaitlistStatus } from "@/lib/afritask";

function AuthGuard({ children }) {
  const token = getSessionToken();
  const legacyUid = localStorage.getItem("afritask.user_id");
  // Legacy anon user (no token) — treat as unauthenticated
  if (!token && !legacyUid) {
    return <Navigate to="/auth/enter-phone" replace />;
  }
  return children;
}

// Wraps `/app/*` — if the authenticated user is waitlisted, bounce to /waitlist.
// A 403 { code: WAITLISTED } response from any API is also caught by the axios
// interceptor in `afritask.js`, so this is defense-in-depth for the initial
// route hit before any request fires.
function WaitlistGuard({ children }) {
  const [state, setState] = useState("checking"); // checking | admitted | waitlisted
  useEffect(() => {
    if (!getSessionToken()) {
      // Anonymous / legacy — let AuthGuard handle it
      setState("admitted");
      return;
    }
    let cancelled = false;
    getWaitlistStatus()
      .then((s) => { if (!cancelled) setState(s.waitlisted ? "waitlisted" : "admitted"); })
      .catch(() => { if (!cancelled) setState("admitted"); }); // fall open — API-level 403 will re-redirect
    return () => { cancelled = true; };
  }, []);
  if (state === "checking") return null;
  if (state === "waitlisted") return <Navigate to="/waitlist" replace />;
  return children;
}

function App() {
  // Aggressively hide the platform-injected "Made with Emergent" badge.
  useEffect(() => {
    // Defensive: unregister any browser-cached service workers that might
    // be intercepting HTTP requests and serving stale JS bundles. This is
    // a one-time safety net for users who ever had an SW registered on
    // this origin — the app itself never registers one now.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => regs.forEach((r) => r.unregister()))
        .catch(() => {});
    }

    const remove = () => {
      const selectors = [
        "#emergent-badge",
        "[id*='emergent-badge']",
        "[class*='emergent-badge']",
        "[class*='emergent-branding']",
        "a[href*='emergent.sh']",
      ];
      selectors.forEach((s) => document.querySelectorAll(s).forEach((n) => n.remove()));
      // Fallback: scan for any anchor whose text says "Made with Emergent"
      document.querySelectorAll("a, div").forEach((n) => {
        const t = (n.textContent || "").trim().toLowerCase();
        if (t === "made with emergent" || t.startsWith("made with emergent")) {
          n.remove();
        }
      });
    };
    remove();
    const obs = new MutationObserver(remove);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
        <AppToaster />
        <MaintenanceBanner />
        <Routes>
          <Route path="/" element={<Landing />} />

          <Route path="/app" element={<AuthGuard><WaitlistGuard><AppShell /></WaitlistGuard></AuthGuard>}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="tasks/:id" element={<TaskDetail />} />
            <Route path="tasks/:id/workspace" element={<TaskWorkspace />} />
            <Route path="applications" element={<Applications />} />
            <Route path="wallet" element={<Wallet />} />
            <Route path="earnings" element={<Earnings />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="profile" element={<Profile />} />
            <Route path="profile-setup" element={<ProfileSetup />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="messages" element={<MessageCentre />} />
          </Route>

          <Route path="/waitlist" element={<Waitlist />} />

          {/* Phase 8.5 — Auth routes */}
          <Route path="/auth/enter-phone" element={<EnterPhone />} />
          <Route path="/auth/enter-pin" element={<EnterPin />} />
          <Route path="/auth/verify-code" element={<VerifyCode />} />
          <Route path="/auth/create-account" element={<CreateAccount />} />
          <Route path="/auth/set-pin" element={<SetPin />} />
          <Route path="/auth/reset-pin" element={<ResetPin />} />

          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/disclaimer" element={<Disclaimer />} />

          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="afriselection" element={<AdminAfriSelection />} />
            <Route path="afriselection/tasks/:taskId/submissions" element={<AdminTaskSubmissions />} />
            <Route path="tasks" element={<AdminTasks />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="withdrawals" element={<AdminWithdrawals />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="selection-logs" element={<AdminSelectionLogs />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="waitlist" element={<AdminWaitlist />} />
          </Route>
          {/* Phase 0 diagnostic page kept for internal testing */}
          <Route path="/poc" element={<POC />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
